// frontend/src/pages/Products/Software.jsx
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ensureNamespace } from '../../i18n/ensurePageI18n';
import softwareHeroImg from '../Products/img/classification.webp';
import imgClassification from '../Products/img/classification.webp';
import imgServer from '../Products/img/server.webp';
import imgArchive from '../Products/img/archive.webp';
import imgReporting from '../Products/img/analysis.webp';
import imgTrend from '../Products/img/analysis+(1).webp';
import imgRoll from '../Products/img/diagram.webp';
import imgFormation from '../Products/img/lock.webp';
import imgIO from '../Products/img/error.webp';
import imgAccess from '../Products/img/document+(1).webp';
import imgMultiLang from '../Products/img/alarm.webp';
import imgDensity from '../Products/img/translation.webp';
import imgInterfaces from '../Products/img/intersect.webp';

const IMAGE_SRC = softwareHeroImg;

const loadI18n = (i18n) => {
  ensureNamespace(i18n, 'software', {
    en: {
      sectionTitle: 'Detection Software',
      sectionSubtitle: 'Advanced Classification • Reporting • Interfaces',
      title: 'DETECTION SOFTWARE',
      intro1: 'An advanced intelligent defect classification engine accurately identifies different types of defects based on their image features, helping users strengthen quality control and further improve production processes. All detected defect data is displayed on-screen in real time and simultaneously stored in the SQL database system, where it can be retrieved, queried, and imported/exported at any time to support production, quality reporting, and related analysis and statistics.',
      intro2: '',
      intro3: '',
      featuresTitle: 'Key Capabilities',
      advancedClassificationTitle: 'Advanced Classification',
      advancedClassificationDesc: 'Precise defect classification using statistical (KNN) and non-linear heuristic methods.',
      sqlDatabaseTitle: 'SQL Database',
      sqlDatabaseDesc: 'Comprehensive defect database with images, positions, and all feature metrics.',
      automaticArchivingTitle: 'Automatic Archiving',
      automaticArchivingDesc: 'Rule-based transfer of defect data to external devices or systems.',
      flexibleReportingTitle: 'Flexible Reporting',
      flexibleReportingDesc: 'Standard and custom tabular/graphical reports of inspection results.',
      trendAnalysisTitle: 'Trend Analysis',
      trendAnalysisDesc: 'Automatic detection and reporting of repeating defects.',
      rollGradingTitle: 'Roll Grading',
      rollGradingDesc: 'Automatic scoring of rolls based on defect class occurrences.',
      formationAnalysisTitle: 'Formation Analysis',
      formationAnalysisDesc: 'Graphical quality display using a Non Uniformity Index.',
      customDigitalIOTitle: 'Custom Digital I/O',
      customDigitalIODesc: 'Generate alarms, auto start/stop jobs, trigger marking events.',
      accessControlTitle: 'Access Control',
      accessControlDesc: 'Remote Internet access for maintenance and software upgrades.',
      multiLanguageGUITitle: 'Multi-Language GUI',
      multiLanguageGUIDesc: 'Interface supports English, German & Chinese.',
      defectDensityTitle: 'Defect Density',
      defectDensityDesc: 'Automatic monitoring based on user-defined criteria.',
      factoryInterfacesTitle: 'Factory Interfaces',
      factoryInterfacesDesc: 'Open architecture via OPC, SQL Query, Ethernet, or custom.',
      viewFull: 'View fullsize'
    },
    zh: {
      sectionTitle: '軟體',
      sectionSubtitle: '進階分類・報表分析・多元介面',
      title: '軟體',
      intro1: '高級缺陷智慧分類引擎，可以根據缺陷的圖像特徵，準確地識別出不同種類的缺陷，幫助用戶有效地提升品質管制，進一步改善生產工藝。所有被檢出的缺陷資料都會即時地在螢幕上顯示，同時存儲到SQL資料庫系統，可隨時調用、查詢和導入匯出，方便生產、品質報告及相關的分析與統計。',
      intro2: '',
      intro3: '',
      featuresTitle: '核心能力',
      advancedClassificationTitle: '進階分類',
      advancedClassificationDesc: '採用統計（KNN）與非線性啟發式方法精準分類缺陷。',
      sqlDatabaseTitle: 'SQL 資料庫',
      sqlDatabaseDesc: '完整保存缺陷影像、位置與全部特徵的資料庫。',
      automaticArchivingTitle: '自動封存',
      automaticArchivingDesc: '依條件自動將缺陷資料傳送至外部裝置或系統。',
      flexibleReportingTitle: '彈性報表',
      flexibleReportingDesc: '標準與自訂報表，以表格與圖形呈現檢測統計。',
      trendAnalysisTitle: '趨勢分析',
      trendAnalysisDesc: '自動偵測並回報重複出現的缺陷。',
      rollGradingTitle: '捲料評等',
      rollGradingDesc: '依缺陷類別出現次數自動給分並評等品質。',
      formationAnalysisTitle: '結構成形分析',
      formationAnalysisDesc: '以非均勻性指數計算結果圖形化呈現品質。',
      customDigitalIOTitle: '自訂數位 I/O',
      customDigitalIODesc: '產生警報、啟停工作、觸發標記等控制動作。',
      accessControlTitle: '存取控制',
      accessControlDesc: '透過網路遠端維護與軟體升級。',
      multiLanguageGUITitle: '多語介面',
      multiLanguageGUIDesc: '支援英文、德文與中文等多語使用者介面。',
      defectDensityTitle: '缺陷密度',
      defectDensityDesc: '依條件自動監控缺陷密度並警示。',
      factoryInterfacesTitle: '工廠介面',
      factoryInterfacesDesc: '開放架構串接 OPC、SQL Query、乙太網或自訂介面。',
      viewFull: '查看原尺寸'
    }
  });
};

function Software() {
  const { t, i18n } = useTranslation();
  loadI18n(i18n);

  const features = useMemo(() => ([
    { k: 'advancedClassification', img: imgClassification },
    { k: 'sqlDatabase', img: imgServer },
    { k: 'automaticArchiving', img: imgArchive },
    { k: 'flexibleReporting', img: imgReporting },
    { k: 'trendAnalysis', img: imgTrend },
    { k: 'rollGrading', img: imgRoll },
    { k: 'formationAnalysis', img: imgFormation },
    { k: 'customDigitalIO', img: imgIO },
    { k: 'accessControl', img: imgAccess },
    { k: 'multiLanguageGUI', img: imgMultiLang },
    { k: 'defectDensity', img: imgDensity },
    { k: 'factoryInterfaces', img: imgInterfaces }
  ]), []);

  return (
    <div>
      <section className="software-section relative text-white py-20">
        <div className="absolute inset-0 bg-black bg-opacity-70" />
        <div className="container mx-auto px-4 relative z-10">
          <h1 className="text-5xl font-bold mb-4">{t('software:sectionTitle')}</h1>
          <p className="text-xl text-white">{t('software:sectionSubtitle')}</p>
        </div>
      </section>
      <main style={{
        padding: 'clamp(1rem,2vw,2.5rem)',
        maxWidth: '1200px',
        margin: '0 auto',
        fontFamily: 'system-ui,-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,"Helvetica Neue",Arial,sans-serif',
        lineHeight: 1.6
      }}>
        <header style={{ marginBottom: '1.5rem' }}>
          <h2 style={{
            fontSize: 'clamp(1.8rem,4vw,2.6rem)',
            margin: 0,
            letterSpacing: '.5px'
          }}>{t('software:title')}</h2>
        </header>
        <section style={{
          display: 'grid',
          gap: '2rem',
          gridTemplateColumns: 'repeat(auto-fit,minmax(min(100%, 300px),1fr))',
          alignItems: 'start',
          marginBottom: '2.5rem'
        }}>
          <article>
            <p>{t('software:intro1')}</p>
            <p>{t('software:intro2')}</p>
            <p>{t('software:intro3')}</p>
          </article>
          {/*<figure style={{ margin: 0, display: 'flex', flexDirection: 'column', gap: '.75rem' }}>*/}
          {/*  <div style={{ position: 'relative', overflow: 'hidden', borderRadius: '8px', boxShadow: '0 4px 16px rgba(0,0,0,.12)' }}>*/}
          {/*    <img*/}
          {/*      src={IMAGE_SRC}*/}
          {/*      alt={t('software:sectionTitle')}*/}
          {/*      style={{ width: '100%', height: 'auto', display: 'block', objectFit: 'cover' }}*/}
          {/*      loading="lazy"*/}
          {/*    />*/}
          {/*  </div>*/}
          {/*</figure>*/}
        </section>
        <section style={{ marginBottom: '3rem' }}>
          <h3 style={{ fontSize: '1.25rem', margin: '0 0 1.25rem', fontWeight: 600 }}>
            {t('software:featuresTitle')}
          </h3>
          <div style={{
            display: 'grid',
            gap: '1.1rem',
            gridTemplateColumns: 'repeat(auto-fit,minmax(210px,1fr))'
          }} aria-label="Software capabilities">
            {features.map(f => (
              <div key={f.k} style={{
                padding: '1rem',
                background: '#f8f9fa',
                border: '1px solid #e2e6ea',
                borderRadius: '8px',
                display: 'flex',
                flexDirection: 'column',
                gap: '.5rem'
              }}>
                {f.img && (
                  <img
                    src={f.img}
                    alt={t(`software:${f.k}Title`)}
                    style={{ width: '40px', height: '40px', objectFit: 'contain' }}
                    loading="lazy"
                  />
                )}
                <strong style={{ fontSize: '.9rem' }}>
                  {t(`software:${f.k}Title`)}
                </strong>
                <p style={{ fontSize: '.7rem', color: '#555', margin: 0 }}>
                  {t(`software:${f.k}Desc`)}
                </p>
                <span style={{
                  fontSize: '.6rem',
                  letterSpacing: '.5px',
                  color: '#888',
                  textTransform: 'uppercase'
                }}>{f.k}</span>
              </div>
            ))}
          </div>
        </section>
      </main>
    </div>
  );
}

export default Software;